import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { BusinessCountryOperation } from '../../types'
import { Check, ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

type Country = { id: string; code: string; name: string }

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  basePath: string
  countries: Country[]
  currentOperations: BusinessCountryOperation[]
}

export function BusinessCountryOperationsSheet({ open, onOpenChange, businessId, basePath, countries, currentOperations }: Props) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const currentCountryIds = currentOperations.map(co => co.country_id)
  const availableCountries = countries.filter(c => !currentCountryIds.includes(c.id))

  function handleAdd(countryId: string) {
    setSubmitting(true)
    router.post(`${basePath}/businesses/${businessId}/country_operations`, {
      country_operation: { country_id: countryId }
    }, {
      onFinish: () => setSubmitting(false),
    })
    setPopoverOpen(false)
  }

  function handleRemove(operationId: string) {
    router.delete(`${basePath}/businesses/${businessId}/country_operations/${operationId}`)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>Countries of Operation</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 px-4">
          {/* Current countries */}
          {currentOperations.length > 0 && (
            <div className="space-y-2">
              {currentOperations.map(co => (
                <div key={co.id} className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50">
                  <span className="text-sm font-medium">{co.country_name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemove(co.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {currentOperations.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No countries added yet</p>
          )}

          {/* Add country */}
          {availableCountries.length > 0 && (
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-full" disabled={submitting}>
                  <ChevronDown className="w-3.5 h-3.5 mr-1.5" />
                  Add country
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width]! p-0 shadow-md" align="start">
                <Command>
                  <CommandInput placeholder="Search countries..." />
                  <CommandList>
                    <CommandEmpty>No countries found.</CommandEmpty>
                    <CommandGroup>
                      {availableCountries.map(country => (
                        <CommandItem
                          key={country.id}
                          value={country.name}
                          onSelect={() => handleAdd(country.id)}
                          className="gap-2"
                        >
                          <span className="truncate">{country.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
